import { AlmconnectionJiraApiClient } from "@/natsstubs/almconnection/jira/client";
import type {
  JiraArtMappingSource,
  JiraFieldMappingForClient,
  JiraFormattedTeamMappingSource,
  JiraTeamMappingField,
} from "@/natsstubs/almconnection/jira/models";
import { AlmsyncJiraApiClient } from "@/natsstubs/almsync/jira/client";
import { keysToSnake } from "@/natsstubs/wire/transform";

import type { NatsRpcBackend } from "../NatsRpcBackend";
import type { UnknownRecord } from "../RpcBackend";
import { errMessage } from "./errMessage";

export function registerJiraConnectionAdapters(nats: NatsRpcBackend): void {
  const client = new AlmconnectionJiraApiClient(nats.caller);
  // boards.get moved to almsync so pi_admins can list boards for the Kanban picker
  // (the only ALM read forced onto NATS).
  const almsyncClient = new AlmsyncJiraApiClient(nats.caller);
  const cid = (args: unknown[]): string => String(args[0]);
  const kwCid = (kwargs?: UnknownRecord): string => String(kwargs?.connection_id);

  const toFieldDict = (fields: JiraTeamMappingField[]): UnknownRecord =>
    Object.fromEntries(
      fields.map((f) => [
        f.key,
        {
          id: f.id,
          key: f.key,
          name: f.name,
          project_ids: f.projectIds,
          schema: f.schema,
          warnings: f.warnings,
        },
      ]),
    );

  const toClientFieldMapping = (
    mapping: JiraFieldMappingForClient | null | undefined,
  ): UnknownRecord =>
    mapping ? { field_id: mapping.fieldId, schema: mapping.schema, values: mapping.values } : {};

  nats.register("alm.jira", "alm-tools", {
    get_oauth_pubkey: async () => (await client.getJiraOauthPublicKey()).publicKey,

    get_oauth_consumer_key: async (_args: unknown[], kwargs?: UnknownRecord) =>
      (await client.getJiraOauthConsumerKey(kwCid(kwargs))).consumerKey,

    get_webhook_endpoint: async (_args: unknown[], kwargs?: UnknownRecord) =>
      (await client.getJiraWebhookEndpoint(kwCid(kwargs))).webhookUrl,

    get_projects: async (args: unknown[]) => (await client.getJiraProjects(cid(args))).projects,

    get_issue_types: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await client.getJiraIssueTypes({ projectId: kwargs?.project_id as string }, cid(args)))
        .issueTypes,

    get_custom_epic_issue_type: async (args: unknown[]) =>
      (await client.getJiraCustomEpicIssueType(cid(args))).issueTypeId,

    set_custom_epic_issue_type: async (args: unknown[]) =>
      client.setJiraCustomEpicIssueType({ issueTypeId: args[1] as string }, cid(args)),

    set_password: async (_args: unknown[], kwargs?: UnknownRecord) =>
      client.setJiraPassword({ password: kwargs?.password as string }, kwCid(kwargs)),

    set_service_account_token: async (_args: unknown[], kwargs?: UnknownRecord) =>
      client.setJiraServiceAccountToken({ token: kwargs?.token as string }, kwCid(kwargs)),

    get_epic_issue_type: async (args: unknown[]) =>
      (await client.getJiraEpicIssueType(cid(args))).issueType,

    get_sprints: async (_args: unknown[], kwargs?: UnknownRecord) =>
      (await client.getJiraSprints(kwCid(kwargs), Number(kwargs?.board_id))).sprints,

    set_team_mapping_sources_from_formatted: async (args: unknown[]) =>
      client.setJiraTeamMappingSourcesFromFormatted(
        { sources: args[2] as JiraFormattedTeamMappingSource[] },
        cid(args),
        Number(args[1]),
      ),

    // Routed here from the generic `almHandlerCommon.setArtMappingSources`
    // (no jira-specific override in almHandlerJira.js); args are
    // [connection_id, art_id, sources[]] same as the WAMP/common shape.
    set_art_mapping_sources: async (args: unknown[]) =>
      client.setJiraArtMappingSources(
        { sources: args[2] as JiraArtMappingSource[] },
        cid(args),
        Number(args[1]),
      ),

    set_art_team_field_mapping_from_formatted: async (args: unknown[]) =>
      client.setJiraArtTeamFieldMappingFromFormatted(
        { mapping: args[2] as JiraFieldMappingForClient },
        cid(args),
        Number(args[1]),
      ),

    set_art_field_mapping_from_formatted: async (args: unknown[]) =>
      client.setJiraArtFieldMappingFromFormatted(
        { mapping: args[2] as JiraFieldMappingForClient },
        cid(args),
        Number(args[1]),
      ),

    set_session_team_field_mapping_from_formatted: async (args: unknown[]) =>
      client.setJiraSessionTeamFieldMappingFromFormatted(
        { mapping: args[2] as JiraFieldMappingForClient },
        String(args[1]),
      ),

    check_project_permissions: async (args: unknown[]) =>
      keysToSnake(await client.checkJiraProjectPermissions(cid(args), String(args[1]))),

    check_project_permissions_for_art: async (args: unknown[]) =>
      keysToSnake(await client.checkJiraProjectPermissionsForArt(cid(args), String(args[1]))),

    check_all_team_mappings: async (args: unknown[], kwargs?: UnknownRecord) =>
      keysToSnake(
        await client.checkJiraTeamMappings({ cachedOnly: Boolean(kwargs?.cached_only) }, cid(args)),
      ),

    check_all_project_permissions_for_art: async (args: unknown[], kwargs?: UnknownRecord) =>
      keysToSnake(
        await client.checkAllJiraProjectPermissionsForArt(
          { cachedOnly: Boolean(kwargs?.cached_only) },
          cid(args),
        ),
      ),

    get_board_mapping: async (args: unknown[]) =>
      keysToSnake((await client.getJiraBoardMapping(cid(args))).mapping),

    get_art_team_field_mapping_formatted: async (args: unknown[]) => ({
      data: toClientFieldMapping(
        (await client.getJiraArtTeamFieldMappingFormatted(cid(args), Number(args[1]))).mapping,
      ),
    }),

    get_art_field_mapping_formatted: async (args: unknown[]) => ({
      data: toClientFieldMapping(
        (await client.getJiraArtFieldMappingFormatted(cid(args), Number(args[1]))).mapping,
      ),
    }),

    get_session_team_field_mapping_formatted: async (args: unknown[]) => ({
      data: toClientFieldMapping(
        (await client.getJiraSessionTeamFieldMappingFormatted(String(args[0]))).mapping,
      ),
    }),

    get_team_mapping_sources_formatted: async (args: unknown[]) => ({
      data: keysToSnake(
        (await client.getJiraTeamMappingSourcesFormatted(cid(args), Number(args[1]))).sources,
      ),
    }),

    get_issue_link_types: async (args: unknown[]) => ({
      issueLinkTypes: (await client.getJiraIssueLinkTypes(cid(args))).linkTypes,
    }),

    get_team_mapping_fields: async (args: unknown[], kwargs?: UnknownRecord) =>
      toFieldDict(
        (
          await client.getJiraTeamMappingFields(
            { projectIds: kwargs?.project_ids as string[] },
            cid(args),
          )
        ).fields,
      ),

    get_team_default_fields: async (args: unknown[], kwargs?: UnknownRecord) =>
      toFieldDict(
        (
          await client.getJiraTeamDefaultFields(
            { projectIds: kwargs?.project_ids as string[] },
            cid(args),
          )
        ).fields,
      ),

    get_art_team_field_mapping_fields: async (args: unknown[], kwargs?: UnknownRecord) =>
      toFieldDict(
        (
          await client.getJiraArtTeamFieldMappingFields(
            { projectIds: kwargs?.project_ids as string[] },
            cid(args),
          )
        ).fields,
      ),

    get_session_backlog_fields: async (args: unknown[], kwargs?: UnknownRecord) =>
      toFieldDict(
        (
          await client.getJiraSessionBacklogFields(
            {
              projectIds: kwargs?.project_ids as string[],
              issueTypeId: kwargs?.issue_type_id as string,
            },
            cid(args),
          )
        ).fields,
      ),

    check_issue_type_for_arts: async (args: unknown[]) => ({
      success: true,
      data: (
        await client.checkJiraIssueTypeForArts(
          { artIds: args[2] as number[] },
          cid(args),
          String(args[1]),
        )
      ).projects.map(keysToSnake),
    }),

    get_fields: async (args: unknown[], kwargs?: UnknownRecord) =>
      Object.fromEntries(
        (
          await client.getJiraFields(
            {
              projectId: kwargs?.project_id as string,
              issueTypeId: kwargs?.issue_type_id as string,
            },
            cid(args),
          )
        ).fields.map((f) => {
          const wrapped = f as { key: string; field: unknown };
          return [wrapped.key, wrapped.field];
        }),
      ),

    get_oauth_url: async (_args: unknown[], kwargs?: UnknownRecord) => {
      try {
        const { url } = await client.getJiraOauthUrl(
          { hostname: kwargs?.hostname as string },
          kwCid(kwargs),
        );
        return { success: true, url };
      } catch (e) {
        const code = (e as { code?: string })?.code;
        const error = errMessage(e);
        if (code === "ALMCONNECTION_JIRA__JIRA_CONFIGURATION_ERROR") {
          return { success: false, errorCode: 1, error };
        }
        if (code === "ALMCONNECTION_JIRA__O_AUTH_AUTHORIZATION_ERROR") {
          return { success: false, errorCode: 2, error };
        }
        throw e;
      }
    },

    get_sprint: async (_args: unknown[], kwargs?: UnknownRecord) =>
      (await client.getJiraSprint(kwCid(kwargs), Number(kwargs?.sprint_id))).sprint ?? false,

    create_test_issue_from_client_format: async (args: unknown[]) => {
      try {
        const r = await client.createJiraTestIssueFromClientFormat(
          {
            projectId: String(args[1]),
            issueTypeId: String(args[2]),
            defaultFields: (args[3] as string | null | undefined) ?? null,
            teamFields: (args[4] as string | null | undefined) ?? null,
            artFields: (args[5] as string | null | undefined) ?? null,
            artTeamFields: (args[6] as string | null | undefined) ?? null,
            teamFieldsFormatted: Boolean(args[7]),
          },
          cid(args),
        );
        return { success: true, issue_key: r.issueKey, url: r.url };
      } catch (e) {
        const code = (e as { code?: string })?.code;
        if (
          code === "ALMCONNECTION_JIRA__JIRA_TEST_ISSUE_PARSE_ERROR" ||
          code === "ALMCONNECTION_JIRA__JIRA_EPIC_LINK_FIELD_MISSING_ERROR" ||
          code === "ALMCONNECTION_JIRA__JIRA_TEST_ISSUE_CREATION_ERROR"
        ) {
          return { success: false, error: errMessage(e) };
        }
        throw e;
      }
    },

    check_link_type: async (args: unknown[]) => {
      try {
        const r = await client.checkJiraLinkType(
          {
            fromId: String(args[1]),
            toId: String(args[2]),
            linkType: String(args[3]),
            fromProjects: args[4] as string[],
            toProjects: args[5] as string[],
          },
          cid(args),
        );
        return {
          success: true,
          from_errors: r.fromErrors.map(keysToSnake),
          to_errors: r.toErrors.map(keysToSnake),
        };
      } catch (e) {
        if ((e as { code?: string })?.code === "ALMCONNECTION_JIRA__JIRA_INVALID_LINK_TYPE_ERROR") {
          return { success: false, error: errMessage(e) };
        }
        throw e;
      }
    },

    finish_oauth: async (_args: unknown[], kwargs?: UnknownRecord) =>
      client.finishJiraOauth({ hostname: kwargs?.hostname as string }, kwCid(kwargs)),

    check_issue_type_fields: async (_args: unknown[], kwargs?: UnknownRecord) => ({
      success: true,
      data: (
        await client.checkJiraIssueTypeFields(
          { projects: kwargs?.projects as string[] },
          kwCid(kwargs),
          kwargs?.issue_type_id as string,
        )
      ).projects.map(keysToSnake),
    }),
  });

  // Per-ART reference Kanban board (Solution Train / multi-ART; ART detail
  // Kanban tab). New endpoints with no WAMP counterpart, so they register under
  // `nats-only` to always route to NATS regardless of the alm-tools rollout flag.
  // args[1] is the art id; consumer reads snake `board_id`, stub returns camelCase
  // `boardId`.
  nats.register("alm.jira", "nats-only", {
    // boards.get lives in almsync now (not the almconnection alm-tools rollout),
    // so it always routes to NATS regardless of those flags — matching the
    // forced-NATS fetchKanbanBoards in the Jira handler for both flag states.
    get_boards: async (
      args: unknown[],
      kwargs: UnknownRecord | undefined,
      _sessionId: unknown,
      timeout?: number,
    ) =>
      (
        await almsyncClient.getJiraBoards(
          {
            projectId: kwargs?.project_id as string,
            boardTypes: kwargs?.board_types as string[] | undefined,
          },
          cid(args),
          timeout,
        )
      ).boards,

    get_art_kanban_board: async (args: unknown[]) => ({
      board_id: (await client.getJiraArtKanbanBoard(cid(args), Number(args[1]))).boardId ?? null,
    }),

    set_art_kanban_board: async (args: unknown[], kwargs?: UnknownRecord) =>
      client.setJiraArtKanbanBoard(
        { boardId: (kwargs?.board_id as number | null | undefined) ?? null },
        cid(args),
        Number(args[1]),
      ),
  });
}
